import {Injectable, NgZone} from "@angular/core";
import {BehaviorSubject, Observable, defer, OperatorFunction, throwError, delay} from 'rxjs';
import { finalize, takeUntil, timeout, catchError } from 'rxjs/operators';
import {SnackbarService} from "./snackbar.service";

@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  private _loading = new BehaviorSubject<boolean>(false);
  public loading$ = this._loading.asObservable().pipe(delay(0));

  constructor(private zone: NgZone,
              private snackbar: SnackbarService) {
  }

  show() {
    this.zone.run(() => {
      this._loading.next(true);
    })
  }

  hide() {
    this.zone.run(() => {
      this._loading.next(false);
    })
  }

  /**
   * Returns an RxJS operator function that wraps an observable with global loading indicator and optional timeout/cancellation logic.
   *
   * When the returned operator is applied to an observable, it:
   *
   * 1. **Shows the Loader:**
   *    - Calls `show()` when the observable is subscribed to.
   *
   * 2. **Enforces an Optional Timeout:**
   *    - If a `timeoutMs` value is provided, the operator applies a timeout to the source observable.
   *    - If the observable does not complete within the specified timeout period, it errors with a TimeoutError.
   *
   * 3. **Supports Cancellation:**
   *    - If a `destroy$` observable is provided (typically a component's onDestroy subject), the operator applies `takeUntil`
   *      so that the subscription is cancelled when `destroy$` emits.
   *
   * 4. **Handles Errors and Hides the Loader:**
   *    - If a timeout error occurs (detected via `error.name === 'TimeoutError'`), it uses SnackbarService to display a generic message.
   *    - Regardless of how the observable terminates (complete, error, or cancellation), `hide()` is called.
   *
   * **Usage Examples:**
   *
   * - Without cancellation or timeout:
   *   ```typescript
   *   this._surveyService.loadAllSurveys(this.dateRange)
   *     .pipe(this.withLoader())
   *     .subscribe(data => { this.surveys = data; });
   *   ```
   *
   * - With cancellation support:
   *   ```typescript
   *   this._surveyService.loadAllSurveys(this.dateRange)
   *     .pipe(this.withLoader(this._clearOnDestroy$))
   *     .subscribe(data => { this.surveys = data; });
   *   ```
   *
   * - With cancellation support and a 10-second timeout:
   *   ```typescript
   *   this._surveyService.loadAllSurveys(this.dateRange)
   *     .pipe(this.withLoader(this._clearOnDestroy$, 10000))
   *     .subscribe(data => { this.surveys = data; });
   *   ```
   *
   * @param destroy$ (optional) An observable that signals when to unsubscribe (e.g., a component's onDestroy subject).
   * @param timeoutMs (optional) A timeout in milliseconds. If provided and the source observable does not complete within this time,
   *                  a timeout error is thrown and a snack bar message is displayed.
   * @returns An operator function to be used in an RxJS pipe.
   */
  withLoader<T>(destroy$?: Observable<any>, timeoutMs?: number): OperatorFunction<T, T> {
    return (source$: Observable<T>): Observable<T> => {
      let result$ = defer(() => {
        this.show();
        return source$;
      });

      if (timeoutMs != null) {
        result$ = result$.pipe(timeout(timeoutMs));
      }

      if (destroy$) {
        result$ = result$.pipe(takeUntil(destroy$));
      }

      result$ = result$.pipe(
        catchError(error => {
          if (error && error.name === 'TimeoutError') {
            this.snackbar.openSnackBar("The operation took too long to complete and was automatically cancelled. Please try again later.");
          }
          return throwError(() => error);
        }),
        finalize(() => this.hide())
      );

      return result$;
    };
  }
}
